"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type CyrillicLanguage,
  type CyrillicSystem,
  iso9ToCyrillic,
  type LetterRow,
  letterRows,
  SYSTEMS_FOR,
  transliterateCyrillic,
} from "@/lib/tool-kit/converters/cyrillic";

/** English strings. A translated content file overrides these through `ui`. */
export const CYRILLIC_TRANSLITERATOR_STRINGS = {
  direction: "Direction",
  toLatin: "Cyrillic → Latin",
  toCyrillic: "Latin → Cyrillic (ISO 9)",
  language: "Language",
  languages: {
    ru: "Russian",
    uk: "Ukrainian",
    be: "Belarusian",
    bg: "Bulgarian",
  },
  inputLabel: "Cyrillic text",
  latinInputLabel: "Latin text in ISO 9",
  latinInputHint:
    "Only ISO 9 can be reversed: it gives every Cyrillic letter its own Latin letter. Diacritics matter — š is ш, s is с.",
  samples: {
    ru: "Лев Николаевич Толстой. «Война и мир»",
    uk: "Леся Українка. «Лісова пісня»",
    be: "Янка Купала. «Шляхам жыцця»",
    bg: "Иван Вазов. «Под игото»",
  },
  latinSample: "Lev Nikolaevič Tolstoj. «Vojna i mir»",
  resultsHeading: "Every system side by side",
  cyrillicHeading: "Cyrillic",
  systems: {
    "ala-lc": "ALA-LC (Library of Congress)",
    bgn: "BGN/PCGN",
    iso9: "ISO 9:1995",
    scholarly: "Scholarly (linguistic)",
    icao: "Passport (ICAO Doc 9303)",
  },
  bgnEditions: {
    ru: "BGN/PCGN 1947",
    uk: "BGN/PCGN 2019 = Ukraine's national system",
    be: "BGN/PCGN 1979",
    bg: "BGN/PCGN 2013 = Bulgaria's official system",
  },
  uses: {
    "ala-lc": "Library catalogs and most English-language reference lists.",
    bgn: "Maps, atlases and English-language place names.",
    iso9: "One Latin letter per Cyrillic letter, so it can be reversed exactly.",
    scholarly: "Linguistics and Slavic studies.",
    icao: "Names in Russian international passports since 2013.",
  },
  unknownLetters:
    "Letters this language's table does not include were left unchanged: {letters}. Check the language setting.",
  enterText: "Type or paste some text to transliterate.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter by letter",
  tableSystem: "System for the letter table",
  letterColumn: "Letter",
  latinColumn: "Romanization",
  noteColumn: "Note",
  notes: {
    "bgn-ru-ye":
      "ye at the start of a word and after a vowel, й, ъ or ь (Екатеринбург → Yekaterinburg)",
    "bgn-ru-yo": "yë at the start of a word and after a vowel, й, ъ or ь",
    "initial-y": "{form} at the start of a word",
    dropped: "not written",
    zgh: "after з it is gh, so зг (zgh) cannot be read as ж (zh)",
    "bg-final-ia": "ия at the end of a word is ia (София → Sofia)",
    "bg-hard-final": "ʺ at the end of a word, in spelling before 1945",
    kept: "kept as an apostrophe",
    historical: "old spelling only",
  },
} as const;

type Strings = typeof CYRILLIC_TRANSLITERATOR_STRINGS;
type Direction = "to-latin" | "to-cyrillic";

const LANGUAGES: readonly CyrillicLanguage[] = ["ru", "uk", "be", "bg"];

function systemName(
  system: CyrillicSystem,
  language: CyrillicLanguage,
  strings: Strings
): string {
  return system === "bgn"
    ? strings.bgnEditions[language]
    : strings.systems[system];
}

function noteText(row: LetterRow, strings: Strings): string {
  if (!row.note) {
    return "";
  }
  const template = strings.notes[row.note];
  return t(template, { form: row.noteValue ?? "" });
}

function OutputCard({
  title,
  use,
  text,
  strings,
}: {
  title: string;
  use: string;
  text: string;
  strings: Strings;
}) {
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{title}</h3>
      <p className={HINT}>{use}</p>
      <p className="whitespace-pre-wrap break-words text-foreground text-lg leading-relaxed">
        {text}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={text}
      />
    </div>
  );
}

function LetterTable({
  language,
  strings,
}: {
  language: CyrillicLanguage;
  strings: Strings;
}) {
  const systems = SYSTEMS_FOR[language];
  const [chosen, setChosen] = useState<CyrillicSystem>("ala-lc");
  const system = systems.includes(chosen) ? chosen : "ala-lc";
  const rows = letterRows(language, system);
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <div className="max-w-sm">
        <SelectField
          id="cy-table-system"
          label={strings.tableSystem}
          onChange={setChosen}
          options={systems.map((each) => ({
            value: each,
            label: systemName(each, language, strings),
          }))}
          value={system}
        />
      </div>
      <DataTable
        head={[strings.letterColumn, strings.latinColumn, strings.noteColumn]}
        rows={rows.map((row) => [
          row.cyrillic,
          row.latin,
          noteText(row, strings),
        ])}
      />
    </section>
  );
}

export default function CyrillicTransliteratorWidget() {
  const strings = useToolUi(CYRILLIC_TRANSLITERATOR_STRINGS);
  const [direction, setDirection] = useState<Direction>("to-latin");
  const [language, setLanguage] = useState<CyrillicLanguage>("ru");
  const [text, setText] = useState<string>(strings.samples.ru);
  const [latin, setLatin] = useState<string>(strings.latinSample);

  function changeLanguage(next: CyrillicLanguage) {
    // Swap the sample for the new language's, but never overwrite the reader's own text.
    const isSample = LANGUAGES.some((each) => strings.samples[each] === text);
    setLanguage(next);
    if (isSample || text.trim() === "") {
      setText(strings.samples[next]);
    }
  }

  const results = SYSTEMS_FOR[language].map((system) => ({
    system,
    result: transliterateCyrillic(text, language, system),
  }));
  // ISO 9 covers every Cyrillic letter, so only the language-specific tables can miss one.
  const unknown = [
    ...new Set(
      results
        .filter((each) => each.system !== "iso9")
        .flatMap((each) => each.result.unknown)
    ),
  ];

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="cy-direction"
        onChange={setDirection}
        options={[
          { value: "to-latin", label: strings.toLatin },
          { value: "to-cyrillic", label: strings.toCyrillic },
        ]}
        value={direction}
      />
      {direction === "to-latin" ? (
        <>
          <PillGroup
            label={strings.language}
            name="cy-language"
            onChange={changeLanguage}
            options={LANGUAGES.map((each) => ({
              value: each,
              label: strings.languages[each],
            }))}
            value={language}
          />
          <TextAreaField
            id="cy-input"
            label={strings.inputLabel}
            onChange={setText}
            prose
            rows={4}
            value={text}
          />
          {text.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
          {unknown.length > 0 ? (
            <Notice>
              {t(strings.unknownLetters, { letters: unknown.join(", ") })}
            </Notice>
          ) : null}
          {text.trim() === "" ? null : (
            <section aria-live="polite" className="space-y-3">
              <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {results.map(({ system, result }) => (
                  <OutputCard
                    key={system}
                    strings={strings}
                    text={result.text}
                    title={systemName(system, language, strings)}
                    use={strings.uses[system]}
                  />
                ))}
              </div>
            </section>
          )}
          <LetterTable language={language} strings={strings} />
        </>
      ) : (
        <>
          <TextAreaField
            hint={strings.latinInputHint}
            id="cy-latin"
            label={strings.latinInputLabel}
            onChange={setLatin}
            prose
            rows={4}
            value={latin}
          />
          {latin.trim() === "" ? (
            <Notice>{strings.enterText}</Notice>
          ) : (
            <section aria-live="polite" className={`${RESULT_CARD} space-y-3`}>
              <h2 className={WIDGET_H2}>{strings.cyrillicHeading}</h2>
              <p className="whitespace-pre-wrap break-words text-foreground text-lg">
                {iso9ToCyrillic(latin)}
              </p>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={iso9ToCyrillic(latin)}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}
